import React, { useRef, useState } from "react";
import { ImagePlus, X, Loader2, Upload, FileCheck2 } from "lucide-react";
import { MKT_GOLD, MKT_TINT, ESTADOS_PRODUCTO, ESTADOS_TIENDA } from "../../lib/marketplace";
import { apiSubirArchivo } from "../../lib/marketplaceApi";
import { humanizeAxiosError } from "../../lib/api";
import { toast } from "sonner";

const INPUT =
  "w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

export function Field({ label, required, hint, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-sm font-semibold text-slate-700">
        {label} {required && <span style={{ color: MKT_GOLD }}>*</span>}
      </span>
      {hint && <span className="block text-xs text-slate-400 mt-0.5">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export function TextInput({ value, onChange, placeholder, type = "text", testid }) {
  return (
    <input
      className={INPUT}
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
  );
}

export function TextArea({ value, onChange, placeholder, rows = 3, testid }) {
  return (
    <textarea
      className={INPUT + " resize-none"}
      rows={rows}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
  );
}

export function SelectInput({ value, onChange, options, placeholder, testid }) {
  return (
    <select className={INPUT} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">{placeholder || "Selecciona…"}</option>
      {options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>
          {o.label ?? o}
        </option>
      ))}
    </select>
  );
}

// Subida REAL de imagen a Object Storage (devuelve URL firmada temporal).
export function ImageUpload({ value, onChange, scope, aspect = "aspect-video", label = "Subir imagen", testid }) {
  const ref = useRef(null);
  const [progreso, setProgreso] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProgreso(0);
    try {
      const res = await apiSubirArchivo(file, scope, setProgreso);
      onChange(res.url);
    } catch (err) {
      toast.error(humanizeAxiosError(err));
    } finally {
      setProgreso(null);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div data-testid={testid}>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} />
      {progreso !== null ? (
        <div className={`${aspect} rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 flex flex-col items-center justify-center gap-2 text-amber-700`}>
          <Loader2 className="animate-spin" size={22} />
          <span className="text-xs font-semibold">Subiendo… {progreso}%</span>
        </div>
      ) : value ? (
        <div className={`relative ${aspect} rounded-xl overflow-hidden border border-slate-200 bg-slate-50`}>
          <img src={value} alt="preview" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className={`w-full ${aspect} rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center gap-1 text-slate-400 hover:border-amber-400 hover:text-amber-600 transition-colors`}
        >
          <ImagePlus size={22} />
          <span className="text-xs font-semibold">{label}</span>
        </button>
      )}
    </div>
  );
}

// Subida REAL de archivo privado (documento / técnico / video). Guarda ref + nombre.
export function FileUpload({ value, nombre, onChange, scope, accept, label = "Seleccionar archivo", testid }) {
  const ref = useRef(null);
  const [progreso, setProgreso] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProgreso(0);
    try {
      const res = await apiSubirArchivo(file, scope, setProgreso);
      onChange(res.url, res.filename);
    } catch (err) {
      toast.error(humanizeAxiosError(err));
    } finally {
      setProgreso(null);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div data-testid={testid}>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} />
      {progreso !== null ? (
        <div className="w-full rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 px-4 py-4 text-sm font-semibold text-amber-700 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Subiendo… {progreso}%
        </div>
      ) : value ? (
        <div className="w-full rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-2 text-emerald-800 font-semibold truncate"><FileCheck2 size={16} /> {nombre || "Archivo subido"}</span>
          <button type="button" onClick={() => onChange("", "")} className="text-slate-400 hover:text-red-600 flex-shrink-0"><X size={15} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} className="w-full rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-sm font-semibold text-slate-500 hover:border-amber-400 hover:text-amber-600 flex items-center justify-center gap-2">
          <Upload size={16} /> {label}
        </button>
      )}
    </div>
  );
}

export function EstadoBadge({ estado, tipo = "producto", testid }) {
  const cfg = (tipo === "tienda" ? ESTADOS_TIENDA : ESTADOS_PRODUCTO)[estado] || {
    label: estado,
    color: "#475569",
    bg: "#F1F5F9",
  };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
      style={{ color: cfg.color, backgroundColor: cfg.bg }}
      data-testid={testid}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cfg.color }} />
      {cfg.label}
    </span>
  );
}

export function Stepper({ pasos, actual }) {
  return (
    <div className="flex items-center gap-2" data-testid="mkt-stepper">
      {pasos.map((p, i) => {
        const done = i < actual;
        const active = i === actual;
        return (
          <React.Fragment key={p}>
            <div className="flex items-center gap-2">
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors"
                style={
                  active || done
                    ? { backgroundColor: MKT_GOLD, borderColor: MKT_GOLD, color: "#fff" }
                    : { borderColor: "#CBD5E1", color: "#94A3B8" }
                }
              >
                {i + 1}
              </span>
              <span
                className={`hidden sm:block text-xs font-semibold ${active ? "text-slate-900" : "text-slate-400"}`}
              >
                {p}
              </span>
            </div>
            {i < pasos.length - 1 && (
              <span className="flex-1 h-0.5 rounded-full" style={{ backgroundColor: done ? MKT_GOLD : "#E2E8F0" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export const SELLER_NOTICE = "Producto creado por un vendedor independiente.";
export const COURSE_NOTICE =
  "Curso creado por un instructor independiente. No pertenece al catálogo oficial de ConstruLink Academia.";
export { MKT_GOLD, MKT_TINT };
